
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Zap, ArrowRight, Compass, Sparkles, ShieldCheck } from 'lucide-react';
import { supabase } from '../lib/supabase';
import VerifiedBadge from '../components/VerifiedBadge';
import { BrutalButton, BrutalCard, BrutalInput } from '../components/BrutalUI';

const MotionDiv = motion.div;
const MotionH1 = motion.h1;
const MotionP = motion.p;

const isReadableHex = (value) => /^#[0-9A-Fa-f]{6}$/.test(String(value || '').trim());
const profileAccent = (profile) => (
  [profile?.accent_color, profile?.button_color, profile?.title_color].find(isReadableHex) || '#FF1E90'
);

const ParallaxCard = ({ children, className }) => {
  return (
    <MotionDiv
      className={className}
      whileHover={{ y: -4 }}
      transition={{ type: 'spring', stiffness: 360, damping: 30 }}
    >
      {children}
    </MotionDiv>
  );
};

const Discover = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchDirectory = useCallback(async () => {
    setLoading(true);
    // Only fetch high-quality, verified profiles for a premium directory feel
    const { data, error } = await supabase
      .from('profiles')
      .select('id, username, full_name, bio, avatar_url, is_verified, accent_color, button_color, title_color, bg_color')
      .eq('is_in_directory', true)
      .eq('is_verified', true)
      .limit(50);

    if (!error && data) {
      setProfiles(data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const frame = requestAnimationFrame(fetchDirectory);
    return () => cancelAnimationFrame(frame);
  }, [fetchDirectory]);

  const filteredProfiles = profiles.filter(p => {
    const cleanQuery = searchQuery.startsWith('@') ? searchQuery.slice(1) : searchQuery;
    const query = cleanQuery.toLowerCase();
    return (
      p.username?.toLowerCase().includes(query) ||
      p.full_name?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="linkit-owned-page landing-page discover-premium-page min-h-screen bg-edge-cream vibrant-grunge recycled-paper-overlay font-sans selection:bg-edge-coral/30 pb-24 text-edge-umber">
      {/* Header Section */}
      <header className="relative pt-20 pb-16 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none">
           <div className="absolute top-20 left-10 w-64 h-64 bg-indigo-500/10 rounded-full blur-[100px]" />
           <div className="absolute bottom-10 right-10 w-96 h-96 bg-rose-500/10 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
           <div className="flex flex-col items-center text-center space-y-6">
              <MotionDiv
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-wrap items-center justify-center gap-3"
              >
                <div className="glass-pill inline-flex items-center gap-2 px-4 py-2 text-edge-umber">
                  <Compass className="text-edge-pink" size={16} />
                  <span className="text-[10px] font-black uppercase tracking-widest text-edge-umber">Creator Directory</span>
                </div>
                <div className="glass-pill inline-flex items-center gap-2 px-4 py-2 text-edge-umber">
                  <ShieldCheck size={14} />
                  <span className="text-[10px] font-black uppercase tracking-widest">Verified Creator Network</span>
                </div>
              </MotionDiv>

              <MotionH1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-5xl md:text-7xl font-black uppercase leading-[0.95] tracking-tight text-edge-umber max-w-3xl anim-misprint"
              >
                Discover the next <span className="text-edge-pink">generation</span> of creators.
              </MotionH1>

              <MotionP
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-edge-umber/70 font-bold text-lg max-w-xl"
              >
                Browse through the most creative minds on Linkit. From artists to engineers, find your inspiration here.
              </MotionP>

              {/* Search Bar */}
              <MotionDiv
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                className="w-full max-w-2xl mt-8 relative"
              >
                <div className="relative flex items-center group">
                  <div className="absolute left-6 text-slate-400 z-10 pointer-events-none">
                    <Search size={24} />
                  </div>
                  <input 
                    type="text" 
                    placeholder="Search by name or @username..." 
                    className="w-full glass-surface rounded-full py-6 pl-16 pr-6 text-edge-umber placeholder:text-edge-umber/40 focus:outline-none focus:ring-2 focus:ring-edge-pink/30 transition-all text-lg"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </MotionDiv>
           </div>
        </div>
      </header>

      {/* Grid Section */}
      <main className="max-w-7xl mx-auto px-6 mt-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="h-80 glass-surface-strong p-8 flex flex-col items-center justify-center gap-4 relative overflow-hidden rounded-2xl">
                <div className="absolute inset-0 bg-edge-coral/5 pointer-events-none opacity-50" />
                <MotionDiv
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 w-full h-full pointer-events-none"
                >
                  <svg className="w-full h-full text-edge-pink/35" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M 40,80 L 40,40 L 80,40" strokeDasharray="5,5" />
                    <path d="M 200,80 L 200,40 L 160,40" strokeDasharray="5,5" />
                    <path d="M 40,240 L 40,280 L 80,280" strokeDasharray="5,5" />
                    <path d="M 200,240 L 200,280 L 160,280" strokeDasharray="5,5" />
                  </svg>
                </MotionDiv>
                <div className="w-24 h-24 border border-white/30 bg-white/10 relative rounded-xl">
                   <div className="absolute inset-0 border-t-2 border-l-2 border-edge-pink animate-[spin_3s_linear_infinite]" />
                </div>
                <div className="h-4 w-32 bg-white/15 border border-white/20 rounded-full mt-2" />
                <div className="h-2 w-24 bg-white/10 border border-white/15 rounded-full" />
                <div className="h-10 w-full border-2 border-dashed border-edge-umber/25 mt-auto relative overflow-hidden">
                  <div className="absolute top-0 left-0 h-full w-1/3 bg-edge-pink/20 animate-[marquee_2s_linear_infinite]" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredProfiles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence>
              {filteredProfiles.map((profile, idx) => (
                <MotionDiv
                  key={profile.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <Link to={`/${profile.username}`} className="block h-full outline-none focus-visible:ring-4 focus-visible:ring-edge-pink/40">
                    <ParallaxCard className="h-full">
                    <BrutalCard 
                      shadow="pink" 
                      className="h-full p-8 flex flex-col items-center text-center space-y-6 group glass-surface-strong"
                    >
                       <div className="relative">
                         <div className="w-24 h-24 bg-white/10 overflow-hidden border border-white/30 rounded-xl shadow-xl transition-transform group-hover:scale-105">
                            {profile.avatar_url ? (
                              <img src={profile.avatar_url} className="w-full h-full object-cover" alt={profile.full_name} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white font-black text-2xl uppercase" style={{ backgroundColor: profileAccent(profile) }}>
                                 {profile.username?.charAt(0)}
                              </div>
                            )}
                         </div>
                         <div className="absolute -bottom-2 -right-2 w-10 h-10 glass-pill flex items-center justify-center shadow-xl rounded-full" style={{ color: profileAccent(profile) }}>
                            <Sparkles size={18} fill="currentColor" />
                         </div>
                       </div>

                       <div className="space-y-2">
                          <h3 className="text-2xl font-black tracking-tighter text-edge-umber transition-colors flex items-center gap-2">
                            {profile.full_name || `@${profile.username}`}
                            {profile.is_verified && <VerifiedBadge profile={profile} size={20} />}
                          </h3>
                          <span className="glass-pill inline-flex px-3 py-1 text-[10px] font-black uppercase tracking-widest text-edge-umber/80">@{profile.username}</span>
                       </div>

                       <p className="text-edge-umber/65 text-sm font-bold line-clamp-2 min-h-[40px]">
                         {profile.bio || "This creator is still crafting their story..."}
                       </p>

                       <div className="pt-2 w-full">
                          <BrutalButton variant="dark" className="w-full py-4 transition-colors">
                            View Profile <ArrowRight size={14} className="ml-2" />
                          </BrutalButton>
                       </div>
                    </BrutalCard>
                    </ParallaxCard>
                  </Link>
                </MotionDiv>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-32 text-center space-y-8">
             <BrutalCard shadow="dark" className="w-24 h-24 flex items-center justify-center text-edge-umber/45">
                <Search size={40} />
             </BrutalCard>
             <div className="space-y-2">
                <h3 className="text-3xl font-black tracking-tighter text-edge-umber anim-misprint">No creators found</h3>
                <p className="text-edge-umber/60 font-bold">Try searching for a different username or alias.</p>
             </div>
             <BrutalButton 
               onClick={() => setSearchQuery('')}
               variant="dark"
               size="lg"
               className="rounded-2xl px-12"
             >
               Clear Search
             </BrutalButton>
          </div>
        )}
      </main>

      {/* Floating Action Button (Go Home) */}
      <Link to="/">
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 px-8 py-4 glass-surface-strong w-max flex items-center gap-3 hover:scale-105 active:scale-95 transition-all z-50 rounded-full">
           <Zap size={18} className="text-edge-pink" fill="currentColor" />
           <span className="font-black text-[10px] uppercase tracking-widest text-edge-umber whitespace-nowrap">Back to Linkit</span>
        </div>
      </Link>
    </div>
  );
};

export default Discover;
